import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "ui/Drawer";
import type { SurfacePresentation } from "types/surfaces";
import { useBodyScrollLock } from "ui/useBodyScrollLock";

/**
 * Props passed into the body render slot so it can drive its own
 * bridge/presentation behavior (e.g., dispatch close, read layout
 * metadata).
 */
export interface SurfaceHostRenderContext {
  /**
   * Programmatically request the host close itself (bubbles through
   * to `onOpenChange(false)`). Use this when the embed body posts
   * `surface:close` back through the bridge.
   */
  close: () => void;
  /** Resolved presentation the host actually rendered. */
  presentation: SurfacePresentation;
}

export interface SurfaceHostProps {
  /** Whether the host surface is currently open. */
  open: boolean;
  /** Notified on every open/close transition. */
  onOpenChange: (open: boolean) => void;
  /**
   * Presentation style. `"inline"` renders in-place (no portal, no
   * overlay) — useful for wide viewports that want the surface body
   * without the drawer/modal chrome.
   */
  presentation: SurfacePresentation;
  /** Render function receiving the host context. */
  children: (ctx: SurfaceHostRenderContext) => React.ReactNode;
  /**
   * Title forwarded to the presentation layer for a11y / sr-only label.
   * Required; pass a localized string.
   */
  title: string;
  /**
   * When true the presentation's built-in title header is hidden
   * visually but remains announced to assistive tech. Defaults to
   * `true` because most embed bodies render their own header.
   */
  hideTitleVisual?: boolean;
  /**
   * Optional test hook forwarded to the presentation root element so
   * Playwright/RTL can query the open host regardless of the chosen
   * presentation.
   */
  testId?: string;
}

/**
 * Centralized host for any CRA→Next bridged surface.
 *
 * - `drawer` → Vaul drawer (mobile-first sheet, existing `frontend/src/ui/Drawer.tsx`).
 * - `modal` → centered rounded modal (fixed width, scroll-locked) —
 *   implemented via a light-weight overlay + rounded container so we
 *   don't couple the host to the heavier `Modal` component's
 *   confirmation/back/portal API (see `frontend/src/ui/Modal.tsx`).
 * - `fullscreen` → `fixed inset-0` panel for viewers/composer flows
 *   that need the entire viewport. Owns body scroll lock + Escape.
 * - `inline` → just renders the children in place (no portal,
 *   no overlay, no scroll lock). The caller is responsible for
 *   sizing.
 *
 * All presentations lock body scroll (except `inline`) and handle
 * Escape via the platform primitives they wrap; the host adds a
 * unified `close` callback so embed bodies can request dismissal
 * without knowing the presentation.
 *
 * Focus-trap concerns:
 *   - Vaul (drawer) handles focus trap + ESC natively.
 *   - `modal` and `fullscreen` branches bind ESC + return focus
 *     to the previously-focused element on close.
 */
export const SurfaceHost: React.FC<SurfaceHostProps> = ({
  open,
  onOpenChange,
  presentation,
  children,
  title,
  hideTitleVisual = true,
  testId,
}) => {
  const close = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const renderContext: SurfaceHostRenderContext = useMemo(
    () => ({ close, presentation }),
    [close, presentation]
  );

  if (presentation === "inline") {
    return open ? <>{children(renderContext)}</> : null;
  }

  if (presentation === "drawer") {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className="max-h-[90vh]"
          data-testid={testId}
          title={hideTitleVisual ? undefined : title}
          titlehidden={hideTitleVisual}
        >
          {/*
           * When hideTitleVisual is true we still need a DrawerTitle
           * inside the content for Vaul's a11y contract; it's
           * visually hidden via the `titlehidden` Drawer prop.
           */}
          {hideTitleVisual ? (
            <DrawerTitle className="sr-only">{title}</DrawerTitle>
          ) : null}
          {children(renderContext)}
        </DrawerContent>
      </Drawer>
    );
  }

  if (presentation === "modal") {
    return (
      <SurfaceModalShell
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        hideTitleVisual={hideTitleVisual}
        testId={testId}
      >
        {children(renderContext)}
      </SurfaceModalShell>
    );
  }

  return (
    <SurfaceFullscreenShell
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      hideTitleVisual={hideTitleVisual}
      testId={testId}
    >
      {children(renderContext)}
    </SurfaceFullscreenShell>
  );
};

interface ShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  hideTitleVisual: boolean;
  testId: string | undefined;
  children: React.ReactNode;
}

/** Lightweight modal shell (non-portalled but fixed-positioned). */
const SurfaceModalShell: React.FC<ShellProps> = ({
  open,
  onOpenChange,
  title,
  hideTitleVisual,
  testId,
  children,
}) => {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const previous = previouslyFocusedRef.current;
      if (previous && previous.isConnected) {
        previous.focus();
      }
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const node = contentRef.current;
    if (!node) return;
    node.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onOpenChange(false);
        }
      }}
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        className="relative m-4 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-eoe-bg shadow-xl outline-none dark:bg-eoe-dark-bg-modal"
      >
        {!hideTitleVisual ? (
          <div className="border-b border-eoe-border-main px-4 py-3 text-center font-semibold text-eoe-text-heading dark:border-eoe-dark-border-main dark:text-eoe-dark-text-heading">
            {title}
          </div>
        ) : null}
        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </div>
  );
};

/** Fullscreen shell (used by stories viewer and stories compose). */
const SurfaceFullscreenShell: React.FC<ShellProps> = ({
  open,
  onOpenChange,
  title,
  hideTitleVisual: _hideTitleVisual,
  testId,
  children,
}) => {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const previous = previouslyFocusedRef.current;
      if (previous && previous.isConnected) {
        previous.focus();
      }
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const node = containerRef.current;
    if (!node) return;
    node.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[70] bg-black outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

export default SurfaceHost;
